import { useEffect } from "react";
import FollowedBrandsService from "@/services/FollowedBrandsService";

const STORAGE_KEY = "pendingFollowAction";

type PendingPayload = { brandId: number; brandName: string };

function safeRead(): PendingPayload | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as PendingPayload) : null;
  } catch {
    return null;
  }
}

function safeClear(): void {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem(STORAGE_KEY);
  }
}

export function writePendingFollowAction(
  brandId: number,
  brandName: string
): void {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ brandId, brandName }));
}

type Params = {
  brandId: number;
  isLoggedIn: boolean;
  statusLoaded: boolean;
  isFollowing: boolean;
  statusCache: Map<number, boolean>;
  setIsFollowing: (v: boolean) => void;
};

export function usePendingFollowAction({
  brandId,
  isLoggedIn,
  statusLoaded,
  isFollowing,
  statusCache,
  setIsFollowing
}: Params): void {
  useEffect(() => {
    if (!isLoggedIn || !statusLoaded || isFollowing) return;
    const pending = safeRead();
    if (!pending || pending.brandId !== brandId) return;
    safeClear();
    FollowedBrandsService.followBrand(pending.brandId, pending.brandName)
      .then(() => {
        setIsFollowing(true);
        statusCache.set(brandId, true);
      })
      .catch(() => {
        /* silent — user can retry manually */
      });
  }, [
    brandId,
    isLoggedIn,
    statusLoaded,
    isFollowing,
    statusCache,
    setIsFollowing
  ]);
}
